import React from 'react';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';

import { Locales, messageToString, PrimaryAnchor } from '@ratehub/base-ui';

import Paths from '../definitions/Paths';
import HeroTabContentListItemContainer from './HeroTabContentListItemContainer';


function HeroTabContentLoans() {
    const intl = useIntl();

    const ctaLink = intl.locale === Locales.FRENCH
        ? (Paths.LOAN_FINDER_FR ?? Paths.LOAN_FINDER_EN)
        : Paths.LOAN_FINDER_EN;

    return (
        <Container className="rh-display-grid rh-gap-1">
            {/* Column 1 */}
            <div className="rh-box-shadow-surround rh-p-1 rh-border-radius-3px rh-display-flex rh-flex-direction-column rh-gap-1">
                <h2 className="rh-fg-blueberry-dark weight-medium rh-m-0">
                    <FormattedMessage
                        id="web-components.HeroTabContentLoans.title"
                        defaultMessage="Compare loan options"
                    />
                </h2>
                <HeroTabContentListItemContainer
                    listItems={listItems}
                />
            </div>

            {/* Column 2 */}
            <div className="col-2 rh-box-shadow-surround rh-p-1 rh-border-radius-3px rh-text-align-center rh-display-flex rh-flex-direction-column rh-justify-content-space-between rh-gap-1">
                <div className="image-wrapper rh-position-relative rh-width-100p rh-height-100p">
                    <img
                        // Placeholder image until we have a proper design
                        src="https://media.istockphoto.com/id/2241209973/photo/man-saving-coins-into-piggy-bank-planning-budget.jpg?s=2048x2048&w=is&k=20&c=kyT_SXRA5ZFmAx7ziSQnUbUGsPZdje27BrStwvbNfyk="
                        alt={messageToString(
                            <FormattedMessage
                                id="web-components.HeroTabContentLoans.img.alt"
                                defaultMessage="Man budgeting finances and counting stacked coins on a wooden table next to a white piggy bank, indicating personal investment, smart spending, and financial planning at home"
                            />,
                            intl,
                        )}
                        className="rh-position-absolute"
                    />
                </div>
                <div className="cta-section">
                    <h2 className="rh-fg-blueberry-dark weight-medium rh-m-0">
                        <FormattedMessage
                            id="web-components.HeroTabContentLoans.ctaSection.title"
                            defaultMessage="LoanFinder tool"
                        />
                    </h2>
                    <p className="rh-m-0">
                        <FormattedMessage
                            id="web-components.HeroTabContentLoans.ctaSection.subtitle"
                            defaultMessage="Instant personal loan matches — see what you qualify for in under a minute."
                        />
                    </p>
                    <PrimaryAnchor
                        href={ctaLink}
                        size="medium"
                        className="rh-mt-1"
                        data-name="hero-tab-content-loan-finder-link"
                    >
                        <FormattedMessage
                            id="web-components.HeroTabContentLoans.ctaSection.button"
                            defaultMessage="find my perfect loans"
                        />
                    </PrimaryAnchor>
                </div>
            </div>
        </Container>
    );
}

const Container = styled.div`
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    grid-template-rows: auto;

    > .col-2 {
        > .image-wrapper {
            min-height: 15rem;

            > img {
                max-width: 100%;
                max-height: 100%;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                object-fit: cover;
                border-radius: 100vh;
                aspect-ratio: 1/1;
            }
        }

        > .cta-section {
            flex: 1 1 auto;
        }
    }
`;

const listItems = [
    {
        iconKey: 'person',
        title: (
            <FormattedMessage
                id="web-components.HeroTabContentLoans.listItems.bestPersonalLoans.title"
                defaultMessage="Best personal loans"
            />
        ),
        subtitle: (
            <FormattedMessage
                id="web-components.HeroTabContentLoans.listItems.bestPersonalLoans.subtitle"
                defaultMessage="Rates as low as 8.99%"
            />
        ),
        href: {
            [Locales.ENGLISH]: Paths.BEST_PERSONAL_LOANS_EN,
            [Locales.FRENCH]: Paths.BEST_PERSONAL_LOANS_FR,
        },
    },
    {
        iconKey: 'car',
        title: (
            <FormattedMessage
                id="web-components.HeroTabContentLoans.listItems.bestCarLoans.title"
                defaultMessage="Best car loans"
            />
        ),
        subtitle: (
            <FormattedMessage
                id="web-components.HeroTabContentLoans.listItems.bestCarLoans.subtitle"
                defaultMessage="Compare car loan options"
            />
        ),
        href: {
            [Locales.ENGLISH]: Paths.BEST_CAR_LOANS_EN,
            [Locales.FRENCH]: Paths.BEST_CAR_LOANS_FR,
        },
    },
    {
        iconKey: 'calculator',
        title: (
            <FormattedMessage
                id="web-components.HeroTabContentLoans.listItems.debtConsolidation.title"
                defaultMessage="Debt consolidation calculator"
            />
        ),
        subtitle: (
            <FormattedMessage
                id="web-components.HeroTabContentLoans.listItems.debtConsolidation.subtitle"
                defaultMessage="Weigh your options to pay down debt"
            />
        ),
        href: {
            [Locales.ENGLISH]: Paths.DEBT_CONSOLIDATION_CALCULATOR_EN,
            [Locales.FRENCH]: Paths.DEBT_CONSOLIDATION_CALCULATOR_FR,
        },
    },
];

export default HeroTabContentLoans;
